import { useTranslation } from "react-i18next";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import type { ManualStepType, StepPayload } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/** A blank payload for a freshly-typed step (used when the server hasn't seeded one yet, or the migrated one was dirty). */
export function defaultPayloadFor(type: ManualStepType): StepPayload | null {
  switch (type) {
    case "question":
      return { kind: "question", question: "", answers: ["", ""], correctIndex: null };
    case "checklist":
      return { kind: "checklist", text: "", items: ["", ""] };
    case "question_with_score":
      return { kind: "question_with_score", question: "", answers: [{ text: "", score: 0, correct: false, gotoAfterSectionName: null }] };
    case "show_score":
      return { kind: "show_score", scoreLevel: 0 };
    default:
      return null; // regular — no interactive payload
  }
}

/**
 * The typed-content draft of an interactive step (ADR-002 Phase 9b; ADR-027 §11 Phase C, owner "pure legacy"):
 * question / checklist / question-with-score / show-score. A local draft seeded from the step's payload (or
 * {@link defaultPayloadFor} when null) that AUTOSAVES like the text states — debounced, through the parent's write
 * gate — the legacy saves on every change and has no Save button. Re-seeds when the SERVER payload changes, but only
 * if the author hasn't diverged from the previous seed (an in-progress edit is never clobbered; panel r2). Keep the
 * QWS answers' `gotoAfterSectionName` — the legacy branch-routing target that rides the wire by answer IDENTITY (the
 * draft preserves it verbatim; there is no branch UI yet). A TYPE change re-seeds unconditionally — the card is not
 * remounted per type any more, and a diverged draft of the OLD kind must never linger under the NEW type's sections
 * (diff-panel Medium #4).
 *
 * Dirtiness is reported through `onDirty` / `onClean` so the parent can fence the exit barrier on an unsaved payload
 * (it marks a DISTINCT id — the payload is not the text), and `flush` lets a lifecycle boundary force the save. Saves
 * are SERIALIZED and each one reconciles against the live draft when it lands: an edit A→B→A while B is in flight
 * would otherwise let B win silently (diff-panel High #1).
 */
export function usePayloadDraft({
  type,
  payload,
  onSave,
  onDirty,
  onClean,
}: {
  type: ManualStepType;
  payload: StepPayload | null;
  onSave: (payload: StepPayload) => Promise<void>;
  onDirty: () => void;
  onClean: () => void;
}): { draft: StepPayload | null; update: (next: StepPayload) => void; flush: () => Promise<void> } {
  const seed = useMemo(() => payload ?? defaultPayloadFor(type), [payload, type]);
  const [draft, setDraft] = useState<StepPayload | null>(seed);
  const lastSaved = useRef<string>(JSON.stringify(seed));
  const live = useRef<StepPayload | null>(seed);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevSeed = useRef(seed);
  const prevType = useRef(type);
  const inFlight = useRef<Promise<void> | null>(null);
  useEffect(() => {
    if (prevType.current !== type) {
      // The step changed TYPE: the old kind's draft is meaningless now — adopt the new seed whatever the divergence.
      prevType.current = type;
      prevSeed.current = seed;
      live.current = seed;
      lastSaved.current = JSON.stringify(seed);
      setDraft(seed);
      onClean();
      return;
    }
    if (prevSeed.current !== seed) {
      // Adopt the fresh server value only when the author was in sync with the old one.
      if (JSON.stringify(live.current) === lastSaved.current) {
        live.current = seed;
        setDraft(seed);
      }
      lastSaved.current = JSON.stringify(seed);
      prevSeed.current = seed;
    }
  }, [seed, type, onClean]);
  const schedule = () => {
    if (timer.current) clearTimeout(timer.current);
    // A failure surfaces via the mutation's error state (the editor's banner) — swallow here so the timer never throws.
    timer.current = setTimeout(() => void flushRef.current().catch(() => {}), 700);
  };
  const flush = async (): Promise<void> => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    // One save at a time: a flush during an in-flight save waits for it, then re-evaluates against what it left.
    if (inFlight.current) await inFlight.current;
    const cur = live.current;
    const json = JSON.stringify(cur);
    if (cur == null || json === lastSaved.current) {
      onClean();
      return;
    }
    const run = onSave(cur).then(() => {
      lastSaved.current = json;
      // Reconcile: the live draft may have moved (or REVERTED) while this save was out — then the server holds the
      // wrong value: stay DIRTY so the barrier blocks until the pending flush (every `update` schedules one, and a
      // flush waits for this save before re-evaluating) resends it (diff-panel High #1).
      if (JSON.stringify(live.current) === json) onClean();
      else onDirty();
    });
    inFlight.current = run;
    try {
      await run;
    } finally {
      if (inFlight.current === run) inFlight.current = null;
    }
  };
  const flushRef = useRef(flush);
  flushRef.current = flush;
  const update = (next: StepPayload) => {
    live.current = next;
    setDraft(next);
    // While a save is in flight the draft is DIRTY regardless: what is on the server is not settled until that save
    // reconciles (an A→B→A revert would otherwise read as clean against the stale `lastSaved`).
    if (inFlight.current == null && JSON.stringify(next) === lastSaved.current) onClean();
    else onDirty();
    schedule();
  };
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  return { draft, update, flush: () => flushRef.current() };
}

/**
 * One SECTION of the payload (ADR-027 §11: the legacy splits a payload across collapsibles — "Checkbox Beschreibung"
 * + "Checkboxen", "Frage" + "Antworten"). Renders nothing for a section the payload kind does not have.
 */
export function PayloadSection({
  section,
  draft,
  disabled,
  onChange,
}: {
  section: "checklistDescription" | "checklistAnswers" | "question" | "answers" | "scoreLevel";
  draft: StepPayload;
  disabled: boolean;
  onChange: (p: StepPayload) => void;
}) {
  if (draft.kind === "checklist") {
    if (section === "checklistDescription") return <ChecklistDescriptionFields draft={draft} disabled={disabled} onChange={onChange} />;
    if (section === "checklistAnswers") return <ChecklistAnswerFields draft={draft} disabled={disabled} onChange={onChange} />;
    return null;
  }
  if (draft.kind === "question") {
    if (section === "question") return <QuestionTextField draft={draft} disabled={disabled} onChange={onChange} />;
    if (section === "answers") return <QuestionAnswerFields draft={draft} disabled={disabled} onChange={onChange} />;
    return null;
  }
  if (draft.kind === "question_with_score") {
    if (section === "question") return <QuestionTextField draft={draft} disabled={disabled} onChange={onChange} />;
    if (section === "answers") return <ScoreAnswerFields draft={draft} disabled={disabled} onChange={onChange} />;
    return null;
  }
  return section === "scoreLevel" ? <ShowScoreFields draft={draft} disabled={disabled} onChange={onChange} /> : null;
}

type Q = Extract<StepPayload, { kind: "question" }>;
type CL = Extract<StepPayload, { kind: "checklist" }>;
type QWS = Extract<StepPayload, { kind: "question_with_score" }>;
type SS = Extract<StepPayload, { kind: "show_score" }>;

function FieldLabel({ children }: { children: string }) {
  return <span className="text-muted-foreground mb-1 block text-xs font-medium">{children}</span>;
}

function QuestionTextField<T extends Q | QWS>({ draft, disabled, onChange }: { draft: T; disabled: boolean; onChange: (p: T) => void }) {
  const { t } = useTranslation();
  return (
    <label className="block">
      <FieldLabel>{t("manualEditor.questionLabel")}</FieldLabel>
      <Input value={draft.question} disabled={disabled} onChange={(e) => onChange({ ...draft, question: e.target.value })} />
    </label>
  );
}

function QuestionAnswerFields({ draft, disabled, onChange }: { draft: Q; disabled: boolean; onChange: (p: Q) => void }) {
  const { t } = useTranslation();
  // A radio group name unique to THIS editor instance — two question steps rendered in the same section must not share a
  // group (else selecting an answer in one visually unchecks the other while their React drafts diverge — panel Medium).
  const groupName = useId();
  return (
    <div>
      <FieldLabel>{t("manualEditor.answersSelectCorrect")}</FieldLabel>
      <ul className="flex list-none flex-col gap-1.5 p-0">
        {draft.answers.map((a, i) => (
          <li key={i} className="flex items-center gap-2">
            <input
              type="radio"
              name={groupName}
              aria-label={t("manualEditor.answerI1IsCorrect", { i1: i + 1 })}
              checked={draft.correctIndex === i}
              disabled={disabled}
              onChange={() => onChange({ ...draft, correctIndex: i })}
              className="accent-primary size-4 shrink-0"
            />
            <Input
              aria-label={t("manualEditor.answerI1", { i1: i + 1 })}
              value={a}
              disabled={disabled}
              onChange={(e) => onChange({ ...draft, answers: draft.answers.map((x, j) => (j === i ? e.target.value : x)) })}
            />
            <RemoveButton
              label={t("manualEditor.removeAnswerI1", { i1: i + 1 })}
              disabled={disabled || draft.answers.length <= 1}
              onPress={() =>
                onChange({
                  ...draft,
                  answers: draft.answers.filter((_, j) => j !== i),
                  correctIndex: draft.correctIndex === i ? null : draft.correctIndex !== null && draft.correctIndex > i ? draft.correctIndex - 1 : draft.correctIndex,
                })
              }
            />
          </li>
        ))}
      </ul>
      <AddButton label={t("manualEditor.addAnswer")} disabled={disabled} onPress={() => onChange({ ...draft, answers: [...draft.answers, ""] })} />
    </div>
  );
}

function ChecklistDescriptionFields({ draft, disabled, onChange }: { draft: CL; disabled: boolean; onChange: (p: CL) => void }) {
  const { t } = useTranslation();
  return (
    <label className="block">
      <FieldLabel>{t("manualEditor.introOptional")}</FieldLabel>
      <Input value={draft.text ?? ""} disabled={disabled} onChange={(e) => onChange({ ...draft, text: e.target.value || null })} />
    </label>
  );
}

function ChecklistAnswerFields({ draft, disabled, onChange }: { draft: CL; disabled: boolean; onChange: (p: CL) => void }) {
  const { t } = useTranslation();
  return (
    <div>
      <FieldLabel>{t("manualEditor.checklistItems")}</FieldLabel>
      <ul className="flex list-none flex-col gap-1.5 p-0">
        {draft.items.map((item, i) => (
          <li key={i} className="flex items-center gap-2">
            <Input
              aria-label={t("manualEditor.itemI1", { i1: i + 1 })}
              value={item}
              disabled={disabled}
              onChange={(e) => onChange({ ...draft, items: draft.items.map((x, j) => (j === i ? e.target.value : x)) })}
            />
            <RemoveButton
              label={t("manualEditor.removeItemI1", { i1: i + 1 })}
              disabled={disabled || draft.items.length <= 1}
              onPress={() => onChange({ ...draft, items: draft.items.filter((_, j) => j !== i) })}
            />
          </li>
        ))}
      </ul>
      <AddButton label={t("manualEditor.addItem")} disabled={disabled} onPress={() => onChange({ ...draft, items: [...draft.items, ""] })} />
    </div>
  );
}

function ScoreAnswerFields({ draft, disabled, onChange }: { draft: QWS; disabled: boolean; onChange: (p: QWS) => void }) {
  const { t } = useTranslation();
  return (
    <div>
        <FieldLabel>{t("manualEditor.answersScored")}</FieldLabel>
        <ul className="flex list-none flex-col gap-1.5 p-0">
          {draft.answers.map((a, i) => (
            <li key={i} className="flex items-center gap-2">
              <Input
                aria-label={t("manualEditor.answerI1Text", { i1: i + 1 })}
                value={a.text}
                disabled={disabled}
                onChange={(e) => onChange({ ...draft, answers: draft.answers.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)) })}
              />
              <Input
                aria-label={t("manualEditor.answerI1Score", { i1: i + 1 })}
                type="number"
                value={String(a.score)}
                disabled={disabled}
                className="w-20 shrink-0"
                onChange={(e) => onChange({ ...draft, answers: draft.answers.map((x, j) => (j === i ? { ...x, score: Number(e.target.value) || 0 } : x)) })}
              />
              <label className="flex shrink-0 items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  aria-label={t("manualEditor.answerI1IsCorrect", { i1: i + 1 })}
                  checked={a.correct}
                  disabled={disabled}
                  onChange={(e) => onChange({ ...draft, answers: draft.answers.map((x, j) => (j === i ? { ...x, correct: e.target.checked } : x)) })}
                  className="accent-primary size-4"
                />
              </label>
              <RemoveButton
                label={t("manualEditor.removeAnswerI1", { i1: i + 1 })}
                disabled={disabled || draft.answers.length <= 1}
                onPress={() => onChange({ ...draft, answers: draft.answers.filter((_, j) => j !== i) })}
              />
            </li>
          ))}
        </ul>
        <AddButton
          label={t("manualEditor.addAnswer")}
          disabled={disabled}
          onPress={() => onChange({ ...draft, answers: [...draft.answers, { text: "", score: 0, correct: false, gotoAfterSectionName: null }] })}
        />
    </div>
  );
}

function ShowScoreFields({ draft, disabled, onChange }: { draft: SS; disabled: boolean; onChange: (p: SS) => void }) {
  const { t } = useTranslation();
  return (
    <label className="block max-w-40">
      <FieldLabel>{t("manualEditor.passingScore")}</FieldLabel>
      <Input
        type="number"
        value={String(draft.scoreLevel)}
        disabled={disabled}
        onChange={(e) => onChange({ ...draft, scoreLevel: Number(e.target.value) || 0 })}
      />
    </label>
  );
}

function AddButton({ label, disabled, onPress }: { label: string; disabled: boolean; onPress: () => void }) {
  return (
    <Button variant="ghost" size="sm" onPress={onPress} isDisabled={disabled} className="mt-1.5 gap-1.5">
      <Plus aria-hidden="true" className="size-3.5" />
      {label}
    </Button>
  );
}

function RemoveButton({ label, disabled, onPress }: { label: string; disabled: boolean; onPress: () => void }) {
  return (
    <Button variant="ghost" size="icon-sm" aria-label={label} onPress={onPress} isDisabled={disabled} className="shrink-0">
      <Trash2 aria-hidden="true" className="size-3.5" />
    </Button>
  );
}
